import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { trackEvent } from "../lib/analytics";

const CartContext = createContext(null);
const KEY = "01l_cart_v1";
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (item) => {
    setItems((prev) => {
      const qty = item.qty && item.qty > 0 ? item.qty : 1;
      const found = prev.find((i) => i.variantId === item.variantId);
      if (found) {
        return prev.map((i) => (i.variantId === item.variantId ? { ...i, qty: i.qty + qty } : i));
      }
      return [...prev, { ...item, qty, key: `${item.variantId}` }];
    });
    const qty = item.qty && item.qty > 0 ? item.qty : 1;
    trackEvent("add_to_cart", {
      value: (item.price * qty) / 100,
      items: [{
        item_id: item.handle,
        item_name: item.title,
        item_variant: `${item.colour} / ${item.size}`,
        price: item.price / 100,
        quantity: qty,
      }],
    });
    setIsOpen(true);
  };

  const setQty = (variantId, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.variantId !== variantId)
        : prev.map((i) => (i.variantId === variantId ? { ...i, qty } : i)),
    );
  };

  const remove = (variantId) => setItems((prev) => prev.filter((i) => i.variantId !== variantId));

  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);

  // abandoned-cart capture: snapshot the basket server-side once we know the shopper's email
  useEffect(() => {
    const email = localStorage.getItem("01l_email");
    if (!items.length || !email) return;
    const t = setTimeout(() => {
      fetch(`${API_URL}/cart-snapshot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          items: items.map((i) => ({
            variantId: i.variantId,
            handle: i.handle,
            title: i.title,
            colour: i.colour,
            size: i.size,
            qty: i.qty,
            price: i.price,
            preorder: !!i.preorder,
            sellingPlanId: i.sellingPlanId || null,
          })),
        }),
      }).catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, add, setQty, remove, clear, count, subtotal, isOpen, openCart: () => setIsOpen(true), closeCart: () => setIsOpen(false) }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
